/**
 * Selector de archivo con el aspecto del panel.
 *
 * El `<input type="file">` nativo lo dibuja el navegador (botón gris, sin relación
 * con el diseño) y no se puede vestir, así que se **oculta** y se usa una etiqueta
 * con el aspecto de un botón; al lado se muestra el nombre del archivo elegido y
 * se puede quitarlo. El archivo se entrega en **base64**, que es como lo esperan
 * los endpoints del panel.
 */

import { Paperclip, X } from 'lucide-react';
import { useId, useRef } from 'react';

import { cn } from '@/lib/utils';

/** Archivo elegido, tal como lo consumen las API del panel. */
export interface ArchivoSeleccionado {
  nombre: string;
  contenido_base64: string;
  /** El fichero original, para los endpoints que suben `multipart/form-data`. */
  archivo: File;
}

interface ArchivoInputProps {
  /** Título del campo (se dibuja encima del control). */
  label?: string;
  /** Texto de ayuda bajo el control. */
  hint?: string;
  /** Tipos aceptados (atributo `accept`). */
  accept?: string;
  /** Nombre del archivo ya elegido, si lo hay. */
  nombre?: string | null;
  /** Texto cuando no hay archivo. */
  placeholder?: string;
  /** Recibe el archivo en base64, o `null` si se quita. */
  onSeleccionar: (archivo: ArchivoSeleccionado | null) => void;
  className?: string;
}

export function ArchivoInput({
  label,
  hint,
  accept = 'application/pdf,image/png,image/jpeg',
  nombre,
  placeholder = 'Ningún archivo seleccionado',
  onSeleccionar,
  className,
}: ArchivoInputProps) {
  const id = useId();
  const entrada = useRef<HTMLInputElement>(null);

  const leer = (archivo: File | undefined) => {
    // Se limpia el input para poder volver a elegir el mismo archivo.
    if (entrada.current) entrada.current.value = '';

    if (!archivo) {
      onSeleccionar(null);

      return;
    }

    const lector = new FileReader();

    lector.onload = () =>
      onSeleccionar({
        nombre: archivo.name,
        contenido_base64: String(lector.result),
        archivo,
      });

    lector.readAsDataURL(archivo);
  };

  return (
    <div className={cn('flex flex-col gap-1.5 text-sm', className)}>
      {label ? <span className="font-medium text-ink-700">{label}</span> : null}

      <div className="flex items-center gap-2">
        <label
          htmlFor={id}
          className="inline-flex cursor-pointer items-center gap-2 rounded-control border border-ink-200 bg-white px-3 py-2 text-sm font-medium text-ink-700 transition-colors hover:border-brand-300 hover:bg-brand-50/40 hover:text-brand-700"
        >
          <Paperclip className="size-4" aria-hidden="true" />
          Elegir archivo
        </label>

        <span
          className={cn('min-w-0 flex-1 truncate text-xs', nombre ? 'text-ink-700' : 'text-ink-400')}
          title={nombre ?? undefined}
        >
          {nombre || placeholder}
        </span>

        {nombre ? (
          <button
            type="button"
            className="inline-flex size-8 shrink-0 items-center justify-center rounded-full text-ink-400 transition-colors hover:bg-ink-100 hover:text-ink-700"
            aria-label="Quitar el archivo"
            onClick={() => {
              if (entrada.current) entrada.current.value = '';

              onSeleccionar(null);
            }}
          >
            <X className="size-4" aria-hidden="true" />
          </button>
        ) : null}
      </div>

      {hint ? <span className="text-xs text-ink-500">{hint}</span> : null}

      <input
        ref={entrada}
        id={id}
        className="sr-only"
        type="file"
        accept={accept}
        onChange={(evento) => leer(evento.target.files?.[0])}
      />
    </div>
  );
}
